<script setup lang="ts">
import RepairSection from '@/components/rc/miner-repairs/repairs-sidebar/RepairSection.vue'
import { useRepairsContext } from '@/composables/rc/drawer-context'

import { useMinerRepairActions } from '@/composables/rc/miner-repair-actions'
import { usePartDetails } from '@/composables/rc/part-details'
import { useStatuses } from '@/composables/rc/statuses'
import { PartStatus } from '@/types/rc/miners/PartStatus'
import { Icon } from '@iconify/vue'
import { useMutationState } from '@tanstack/vue-query'
import { capitalize } from 'lodash'
import { computed, ref, watchEffect } from 'vue'

const { minerId, selectedPart } = useRepairsContext()
const { partStatus, hasStatus, isReplacement, partId, movedToInventory } = usePartDetails(minerId, selectedPart)

const selectedPartStatus = ref<PartStatus | null>()
const editing = ref(false)

const { partStatuses } = useStatuses()
const filteredPartStatuses = computed(() =>
  partStatuses.value?.filter((status: string) => status !== PartStatus.Replaced)
)

// State sync
watchEffect(() => {
  selectedPartStatus.value = partStatus.value
})

// Eye out spare-part-replacement, as it affects status
const data = useMutationState({
  filters: { mutationKey: ['spare-part-replacement', minerId.value] },
})
const replacementActionPending = computed(() => data.value?.some((item) => item.status === 'pending'))

const { updatePartStatusMutation } = useMinerRepairActions(minerId)

const onChangePartStatus = () => {
  if (!partId.value) return
  updatePartStatusMutation.mutate(
    {
      part_id: partId.value,
      partName: selectedPart.value,
      status: selectedPartStatus.value ?? null,
    },
    {
      onError: () => {
        selectedPartStatus.value = partStatus.value
      },
      onSettled: () => {
        if (editing.value) editing.value = false
      },
    }
  )
}

const stepChecked = computed(
  () => !!partStatus.value && ![PartStatus.Fail, PartStatus.Blocked].includes(partStatus.value)
)

const settingStatusIsDisabled = computed(
  () => !partId.value || replacementActionPending.value || (hasStatus.value && !editing.value) || isReplacement.value
)
</script>

<template>
  <RepairSection title="Part Status" :step="4" :active="stepChecked">
    <div class="w-full flex items-center gap-2">
      <el-select
        v-model="selectedPartStatus"
        :disabled="settingStatusIsDisabled"
        clearable
        @change="onChangePartStatus"
      >
        <template v-if="replacementActionPending" #label> Updating... </template>
        <el-option v-for="status in filteredPartStatuses" :key="status" :value="status">{{
          capitalize(status)
        }}</el-option>
      </el-select>
      <Icon
        v-if="hasStatus && !editing && !isReplacement && !movedToInventory"
        class="cursor-pointer"
        icon="material-symbols:edit-outline"
        @click="editing = true"
      />
      <Icon
        v-if="hasStatus && editing"
        class="cursor-pointer"
        icon="material-symbols:check-rounded"
        @click="editing = false"
      />
    </div>
  </RepairSection>
</template>
